import React, { memo, useEffect, useMemo, useRef } from 'react';
import PlaceholderAvatar from './PlaceholderAvatar';

/**
 * The @mention picker, anchored above the composer.
 *
 * Replaces a bare `<ul>` of avatar-and-name rows that was mouse-only. Four things
 * were wrong with it beyond the styling:
 *
 * - **No keyboard.** The list opened while the caret was in the composer, so the
 *   hands were already on the keys, and the only way to accept a suggestion was to
 *   let go of them and click. Arrow keys, Enter/Tab and Escape are the whole
 *   interaction now; the mouse is the fallback.
 * - **No active row**, so nothing said what Enter would do.
 * - **Unbounded.** It rendered one row per matching member — a 500-member channel
 *   painted 500 rows into a 200px box on the first `@`.
 * - **Nothing when empty.** A query matching no one left the panel mounted but
 *   blank, which reads as broken rather than as "no matches".
 *
 * Presentation and keyboard handling only: the caret arithmetic, the draft text and
 * the mention id list stay with the composer that owns them. `activeIndex` is
 * controlled for the same reason — the parent has to clamp it when the query
 * narrows the list under the cursor.
 */

/** Rows rendered at once. The container scrolls; the rest are reachable by arrow key. */
export const MENTION_VISIBLE_ROWS = 8;

/** Emphasise the matched run so it is obvious WHY a row is in the list. */
const HighlightedName = ({ name, query }) => {
  const at = query ? name.toLowerCase().indexOf(query.toLowerCase()) : -1;
  if (at === -1) return <>{name}</>;
  return (
    <>
      {name.slice(0, at)}
      <b className="mention-row-match">{name.slice(at, at + query.length)}</b>
      {name.slice(at + query.length)}
    </>
  );
};

const MentionSuggestions = ({
  users,
  query = '',
  activeIndex = 0,
  onSelect,
  onActiveIndexChange,
  listboxId = 'mention-listbox',
}) => {
  const listRef = useRef(null);
  const activeRef = useRef(null);

  // Keep the highlighted row in view when it moves by keyboard rather than by
  // pointer — arrowing past the eighth row must scroll, not walk off the edge.
  // `block: 'nearest'` so it only scrolls when the row is actually out of view.
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const visible = useMemo(() => users ?? [], [users]);

  return (
    <div className="mention-panel" role="presentation">
      <div className="mention-panel-head">
        <span className="mention-panel-title">
          {visible.length > 0 ? 'Mention a member' : 'No members match'}
        </span>
        {visible.length > 0 && (
          <span className="mention-panel-hint" aria-hidden="true">
            <kbd>↑</kbd>
            <kbd>↓</kbd>
            to move
            <kbd>↵</kbd>
            to select
            <kbd>esc</kbd>
            to dismiss
          </span>
        )}
      </div>

      {visible.length === 0 ? (
        <p className="mention-empty">
          Nobody in this group matches <b>“{query}”</b>. Keep typing to search, or press
          <kbd>esc</kbd> to dismiss.
        </p>
      ) : (
        <ul
          ref={listRef}
          className="mention-list custom-scrollbar"
          id={listboxId}
          role="listbox"
          aria-label="Group members"
          style={{ '--mention-rows': MENTION_VISIBLE_ROWS }}
        >
          {visible.map((user, index) => {
            const isActive = index === activeIndex;
            return (
              <li
                key={user.id}
                ref={isActive ? activeRef : null}
                id={`${listboxId}-opt-${user.id}`}
                role="option"
                aria-selected={isActive}
                className={`mention-row ${isActive ? 'is-active' : ''}`}
                // `onMouseDown` rather than `onClick`: the composer input loses focus
                // on mousedown, and a blur handler that closes the panel would remove
                // this node before the click ever landed.
                onMouseDown={(event) => {
                  event.preventDefault();
                  onSelect?.(user);
                }}
                onMouseEnter={() => onActiveIndexChange?.(index)}
              >
                {user.image ? (
                  <img
                    className="mention-row-avatar"
                    src={user.image}
                    alt=""
                    width={28}
                    height={28}
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <PlaceholderAvatar size={28} />
                )}
                <span className="mention-row-name">
                  <HighlightedName name={user.name} query={query} />
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default memo(MentionSuggestions);
